/* ════ LIST VIEW (chronological) ═══════════════════════════ */
import { state } from './state.js';
import { $ } from './dom.js';
import { MO } from './config.js';
import { catClass, catVars, catLabel } from './categories.js';
import { pdate } from './utils/date.js';
import { matchFilter } from './filtermatch.js';
import { openEdit } from './modals/task.js';
import { canSeeTask } from './privacy.js';
import { gbp } from './utils/money.js';
import { sb } from './supabase/client.js';
import { setSyncDot, logActivity } from './supabase/data.js';
import { showToast } from './utils/toast.js';
import { confettiFrom } from './utils/confetti.js';

const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const fmt = d => { const x = pdate(d); return `${x.getDate()} ${MO[x.getMonth()]} ${x.getFullYear()}`; };

export function renderList() {
  const wrap = $('list-rows'); if (!wrap) return;
  const tasks = state.tasks.filter(matchFilter).slice()
    .sort((a, b) => a.start.localeCompare(b.start) || a.end.localeCompare(b.end));
  if (tasks.length === 0) { wrap.innerHTML = '<div class="list-empty">No tasks match the current filters.</div>'; return; }
  // Totals across visible tasks (private tasks you can't see are excluded, so
  // the totals match the figures shown). Hours + cost are grouped by client.
  let totalCost = 0, totalHours = 0;
  const byClient = new Map();
  const rows = tasks.map(t => {
    const res = state.resources.find(r => r.id === t.resource);
    if (!canSeeTask(t)) return `<div class="list-row private" data-id="${t.id}">
      <div class="lr-cat"></div>
      <div class="lr-dates"><span>${fmt(t.start)}</span><span class="lr-arrow">→</span><span>${fmt(t.end)}</span></div>
      <div class="lr-main"><div class="lr-name">🔒 Private</div></div>
      <div class="lr-right">${res ? `<span class="lr-team">${esc(res.name)}</span>` : ''}</div>
      <div class="lr-inv"></div>
      <div class="lr-cost">—</div>
    </div>`;
    const stl = t.subtasks || [], total = stl.length, done = stl.filter(s => s.done).length;
    const imp = t.importance || 'normal';
    const cost = Number(t.cost) || 0; totalCost += cost;
    const hrs = Number(t.hours) || 0; totalHours += hrs;
    if (hrs || cost) { const c = t.client || 'No client'; const g = byClient.get(c) || { h: 0, cost: 0 }; g.h += hrs; g.cost += cost; byClient.set(c, g); }
    return `<div class="list-row" data-id="${t.id}">
      <div class="lr-cat ${catClass(t.cat)}" style="${catVars(t.cat)}"></div>
      <div class="lr-dates"><span>${fmt(t.start)}</span><span class="lr-arrow">→</span><span>${fmt(t.end)}</span>${t.half && t.half !== 'none' ? ` <span class="lr-half">${t.half}</span>` : ''}</div>
      <div class="lr-main">
        <div class="lr-name${t.completed ? ' done' : ''}">${t.completed ? '✓ ' : ''}${t.client ? '<b>' + esc(t.client) + '</b> — ' : ''}${esc(t.name)}${imp === 'high' ? ' <span class="imp-tag imp-high">! High</span>' : ''}${imp === 'low' ? ' <span class="imp-tag imp-low">↓ Low</span>' : ''}</div>
        ${(res || total > 0 || t.hours) ? `<div class="lr-sub">${[res ? esc(res.name) : '', total > 0 ? `${done}/${total} subtasks` : '', t.hours ? `${Number(t.hours)}h` : ''].filter(Boolean).join(' · ')}</div>` : ''}
        ${(t.tags || []).length ? `<div class="lr-tags">${t.tags.map(tg => `<span class="card-tag">${esc(tg)}</span>`).join('')}</div>` : ''}
      </div>
      <div class="lr-right">
        ${t.team ? `<span class="lr-team">${esc(t.team)}</span>` : ''}
        <div class="lr-catlbl ${catClass(t.cat)}" style="${catVars(t.cat)}">${esc(catLabel(t.cat))}</div>
      </div>
      <div class="lr-inv">
        <label class="inv-chk ready${t.ready_invoice ? ' on' : ''}" title="Ready to invoice"><input type="checkbox" class="inv-ready"${t.ready_invoice ? ' checked' : ''}><span>Ready</span></label>
        <label class="inv-chk done${t.invoiced ? ' on' : ''}" title="Invoiced"><input type="checkbox" class="inv-done"${t.invoiced ? ' checked' : ''}><span>Invoiced</span></label>
      </div>
      <div class="lr-cost">${cost ? gbp(cost) : '—'}</div>
    </div>`;
  }).join('');
  const chips = [...byClient.entries()].sort((a, b) => b[1].cost - a[1].cost || b[1].h - a[1].h)
    .map(([c, g]) => `<span class="lh-chip">${esc(c)}${g.h ? ` <b>${+g.h}h</b>` : ''}${g.cost ? ` · <b>${gbp(g.cost)}</b>` : ''}</span>`).join('');
  const byBlock = byClient.size ? `<div class="list-hours">
      <span class="lh-lbl">By client</span>
      <div class="lh-chips">${chips}</div>
      <span class="lh-total">Total ${+totalHours}h · ${gbp(totalCost)}</span>
    </div>` : '';
  const ready = tasks.filter(t => canSeeTask(t) && t.ready_invoice && !t.invoiced);
  const readyCost = ready.reduce((s, t) => s + (Number(t.cost) || 0), 0);
  const sendBlock = ready.length ? `<div class="list-send">
      <button class="btn btn-red" id="send-inv">📧 Send ${ready.length} to invoicing · ${gbp(readyCost)}</button>
      <span class="ls-note">emails the task${ready.length === 1 ? '' : 's'} marked Ready (not yet invoiced) to the invoice inbox</span>
    </div>` : '';
  wrap.innerHTML = rows
    + `<div class="list-total"><span>Total — ${tasks.length} task${tasks.length === 1 ? '' : 's'}</span><span class="lt-val">${gbp(totalCost)}</span></div>`
    + byBlock + sendBlock;
  const sendBtn = $('send-inv'); if (sendBtn) sendBtn.addEventListener('click', sendInvoiceBatch);
  wrap.querySelectorAll('.list-row').forEach(row => row.addEventListener('click', () => openEdit(row.dataset.id)));
  // Invoice tick boxes: don't let their clicks open the card.
  wrap.querySelectorAll('.lr-inv').forEach(el => el.addEventListener('click', e => e.stopPropagation()));
  wrap.querySelectorAll('.inv-ready').forEach(cb => cb.addEventListener('change', () => setInvoice(cb.closest('.list-row').dataset.id, { ready_invoice: cb.checked })));
  wrap.querySelectorAll('.inv-done').forEach(cb => cb.addEventListener('change', () => setInvoice(cb.closest('.list-row').dataset.id, { invoiced: cb.checked })));
}

async function setInvoice(id, patch) {
  const t = state.tasks.find(x => String(x.id) === String(id));
  if (t) Object.assign(t, patch);   // optimistic — the checkbox already shows the new state
  setSyncDot('busy');
  const { error } = await sb.from('tasks').update(patch).eq('id', id);
  if (error) { setSyncDot('err'); showToast('Update failed: ' + error.message); return; }
  setSyncDot('ok');
  const label = 'ready_invoice' in patch ? (patch.ready_invoice ? 'ready to invoice' : 'not ready to invoice')
    : (patch.invoiced ? 'invoiced' : 'un-invoiced');
  logActivity(label, 'task', t?.name || '');
  renderList();   // refresh the "Send to invoicing" count
}

// Email one consolidated summary of every visible task marked Ready-but-not-Invoiced.
async function sendInvoiceBatch() {
  const list = state.tasks.filter(matchFilter).filter(t => canSeeTask(t) && t.ready_invoice && !t.invoiced);
  if (!list.length) { showToast('No tasks marked ready to invoice'); return; }
  const total = list.reduce((s, t) => s + (Number(t.cost) || 0), 0);
  if (!confirm(`Email ${list.length} task(s) (${gbp(total)}) to the invoice inbox?`)) return;
  const tasksPayload = list.map(t => ({
    id: t.id, name: t.name, client: t.client || '', job: t.job || '',
    cost: Number(t.cost) || 0, hours: Number(t.hours) || 0,
    resource: state.resources.find(r => r.id === t.resource)?.name || '',
    start: t.start, end: t.end,
  }));
  setSyncDot('busy');
  try {
    const { data, error } = await sb.functions.invoke('notify-invoice', { body: { tasks: tasksPayload, by: state.currentUser?.email || '' } });
    if (error) { setSyncDot('err'); showToast('Send failed — the notify-invoice function isn’t reachable (deployed?)'); return; }
    if (!data || !data.ok) { setSyncDot('err'); showToast('Send failed: ' + ((data && data.error) || 'email was rejected')); return; }
    setSyncDot('ok'); showToast(`📧 Sent ${list.length} task(s) to invoicing ✓`);
    const b = $('send-inv');
    if (b) {
      const label = b.textContent;
      b.textContent = '✓ Sent to invoicing'; b.classList.add('is-sent'); b.disabled = true;
      confettiFrom(b, { emoji: ['💷', '🧾'] });
      setTimeout(() => { if (b.isConnected) { b.textContent = label; b.classList.remove('is-sent'); b.disabled = false; } }, 2200);
    }
    logActivity('sent to invoicing', 'task', `${list.length} tasks · ${gbp(total)}`);
  } catch (e) { setSyncDot('err'); showToast('Send failed: ' + e.message); }
}
